import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { adminConfig } from 'src/environments/environment';
import { IDisableItemsDto } from '../common/models/disableItemsDto';
import { FacilityItemInformation } from '../common/models/facilityItemInformation';
import { Operation } from '../common/models/operation';
import { OrderableItemPrepLocation } from '../common/models/orderableItemPrepLocation';
import { LoggingService } from './logging.service';
import { UnimportedItem } from '../common/models/unimportedItem';
import { SoldQuantityResponse } from '../common/models/soldQuantityDto';

export interface BatchPatchRequest {
  facilityIds: string[];
  patches: {
    operations: Operation[];
    commerceItemNumbers: string[];
  };
}

export interface BatchPatchResponse {
  results: {
    facilityId: string;
    commerceItemNumber: string;
    success: boolean;
    error?: any;
  }[];
}

@Injectable({ providedIn: 'root' })
export class ItemsService {
  private baseUriV2: string = adminConfig.baseAdminApiUri;

  constructor(
    private http: HttpClient,
    private logger: LoggingService,
  ) {}

  public getItems(facilityId: string): Observable<FacilityItemInformation[]> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http.get<FacilityItemInformation[]>(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/items',
      options,
    );
  }

  public getMenuItemsForDepartment(facilityId: string, departmentName: string): Observable<FacilityItemInformation[]> {
    return this.getItems(facilityId).pipe(map((items) => items.filter((x) => x.departmentName === departmentName)));
  }

  public getItem(facilityId: string, commerceItemNumber: string): Observable<FacilityItemInformation> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http.get<FacilityItemInformation>(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/items/' + commerceItemNumber,
      options,
    );
  }

  public patchOrderableItems(request: BatchPatchRequest): Observable<BatchPatchResponse> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .patch<BatchPatchResponse>(this.baseUriV2 + '/api/organizations/1/PatchOrderableItems', request, options)
      .pipe(catchError(this.handleError));
  }

  public getModifiers(facilityId: string): Observable<FacilityItemInformation[]> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http.get<FacilityItemInformation[]>(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/modifiers',
      options,
    );
  }

  public getUnimportedItems(facilityId: string): Observable<UnimportedItem[]> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http.get<UnimportedItem[]>(
      this.baseUriV2 + '/api/organizations/1/facilities/' + facilityId + '/orderableitems/unimported',
      options,
    );
  }

  public patchOrderableItemPrepLocation(
    facilityId: string,
    commerceItemNumber: string,
    operations: Operation[],
  ): Observable<Object> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.patch(
      this.baseUriV2
        + '/api/organizations/1/facilities/'
        + facilityId
        + '/preplocations/orderableitems/'
        + commerceItemNumber,
      JSON.stringify(operations),
      options,
    );
  }

  public importOrderableItemPrepLocation(orderableItemPrepLocation: OrderableItemPrepLocation): Observable<Object> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.put(
      this.baseUriV2
        + '/api/organizations/1/facilities/'
        + orderableItemPrepLocation.facilityId
        + '/preplocations/orderableitems/'
        + orderableItemPrepLocation.commerceItemNumber,
      JSON.stringify(orderableItemPrepLocation),
      options,
    );
  }

  public getParentItems(commerceItemNumber: string): Observable<FacilityItemInformation[]> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http.get<FacilityItemInformation[]>(
      this.baseUriV2 + '/api/organizations/1/modifiers/' + commerceItemNumber + '/parents',
      options,
    );
  }

  public sortItemsByFilter(items: FacilityItemInformation[], filterBy: string): string[] {
    const filter = [...new Set(items.map((i: Record<string, any>) => i[filterBy]))];
    filter.sort((a, b) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });
    return filter;
  }

  // upsert item availability exception
  public setDisabledItems(disabledItems: IDisableItemsDto[]): Observable<Object> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http
      .post<IDisableItemsDto>(
        this.baseUriV2 + '/api/itemavailabilityexceptions',
        JSON.stringify(disabledItems),
        options,
      )
      .pipe(catchError(this.handleError));
  }

  //item availability exception delete
  public setEnabledItems(organizationId: number, facilityId: string, commerceItemNumber: string): Observable<Object> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http
      .delete(
        this.baseUriV2
          + '/api/organizations/'
          + organizationId
          + '/facilities/'
          + facilityId
          + '/commerceItems/'
          + commerceItemNumber
          + '/itemavailabilityexceptions',
        options,
      )
      .pipe(catchError(this.handleError));
  }

  public getSoldQuantityForItem(
    organizationId: number,
    facilityId: string,
    commerceItemNumber: string,
    startDate: string,
    endDate: string,
  ): Observable<SoldQuantityResponse> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    return this.http
      .get<SoldQuantityResponse>(
        this.baseUriV2
          + '/api/organizations/'
          + organizationId
          + '/facility/'
          + facilityId
          + '/orderableitem/'
          + commerceItemNumber
          + '/soldquantity'
          + '?startDate='
          + startDate
          + '&endDate='
          + endDate,
        options,
      )
      .pipe(catchError(this.handleError));
  }

  public getDurableStatus(instanceId: string, code: string) {
    return this.http.get(
      this.baseUriV2
        + '/api/organizations/preplocations/orderableitems/getpatchorderableitemsstatus/'
        + instanceId
        + '/'
        + code,
      { observe: 'response' },
    );
  }

  public syncGroup(facilityId: string, commerceItemNumber: string): Observable<Object> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post(
        this.baseUriV2
          + '/api/organizations/1/facilities/'
          + facilityId
          + '/preplocations/orderableitems/'
          + commerceItemNumber
          + '/syncgroup',
        {},
        options,
      )
      .pipe(catchError(this.handleError));
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    console.log(err);
    this.logger.logException(err);
    return throwError(() => new Error(err.message));
  }
}
